import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { BrandInsightsPageContext, ChatContextItem } from '@trinity/chat';
import { ActivePageContext, ChatContextItemsContext, ChatContextItemsLoadingContext, ChatDefaultContextItemContext, ChatFaqContextItemContext } from '@trinity/chat-components/components/Chat';
import { useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import { useLocation } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { useGroupId } from '@trinity/chat-components/containers/Chat/selectors';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveApplicationIdentifier } from '@trinity/chat-components/containers/UserApplications/index.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { buildInitialChatContext } from './build-default-context';

type Props = {
  children: React.ReactNode;
};

export const ChatAgentsContainer = (props: Props) => {

  const brandIdentifier = useActiveBrandIdentifier();
  const applicationIdentifier = useActiveApplicationIdentifier();
  const apiClient = useChatApiClient();
  const location = useLocation();
  const groupId = useGroupId();
  const features = useGroupFeatures();

  const [defaultContext, setDefaultContext] = useState<BrandInsightsPageContext.Item | null>(null);
  const [activePageId, setActivePageId] = useState<number | null>(null);
  const hasInitializedPageId = useRef(false);

  const q = useQuery<ChatContextItem[]>({
    queryKey: ['chat-contexts', brandIdentifier, applicationIdentifier],
    queryFn: () => {
      return apiClient.chat.getChatContexts({ brandIdentifier, applicationIdentifier }).then(res => res.items);
    },
    // placeholderData: [],
    enabled: !!brandIdentifier && !!applicationIdentifier,
  });

  useEffect(() => {
    if (q.isSuccess && q.data) {
      const firstItem = q.data[0];
      if (firstItem?.pageId != null && !hasInitializedPageId.current) {
        hasInitializedPageId.current = true;
        setActivePageId(firstItem.pageId);
      }
      setDefaultContext(buildInitialChatContext({
        contextItems: q.data,
        groupId,
        pathname: location.pathname,
      }));
    }
  }, [q.data, q.isSuccess, groupId, location.pathname]);

  const clearPage = useCallback(() => {
    setActivePageId(null);
  }, []);

  const setPage = useCallback((pageId: number | null) => {
    setActivePageId(pageId);
  }, []);

  const activePageCtx = useMemo(() => ({
    pageId: activePageId,
    clearPage,
    setPage,
  }), [activePageId, clearPage, setPage]);

  const isLoading = q.isLoading;

  const faqContext = useMemo<ChatContextItem | null>(() => {
    const id = features.faqChatContextIdentifier;
    if (!id || !q.data) return null;
    return q.data.find(item => item.identifier === id) ?? null;
  }, [q.data, features.faqChatContextIdentifier]);

  if (isLoading || !q.data || !defaultContext) {
    return null;
  }

  return (
    <ActivePageContext.Provider value={activePageCtx}>
      <ChatContextItemsContext.Provider value={q.data}>
        <ChatContextItemsLoadingContext.Provider value={isLoading}>
          <ChatDefaultContextItemContext.Provider value={defaultContext}>
            <ChatFaqContextItemContext.Provider value={faqContext}>
              {props.children}
            </ChatFaqContextItemContext.Provider>
          </ChatDefaultContextItemContext.Provider>
        </ChatContextItemsLoadingContext.Provider>
      </ChatContextItemsContext.Provider>
    </ActivePageContext.Provider>
  );
};